import React, { useCallback, useEffect, useState } from 'react'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Form, Input, Row, Select, Tabs } from 'antd';
import { Button } from 'react-bootstrap';
import { NotificationTrigger } from '../../models/Notification';
import IconButton from '../common/IconButton';
import ScriptEditor from '../common/ScriptEditor';
import { notificationTypesOptions } from '../../utils';

const methodsOptions = [
  { label: 'GET', value: 'GET' },
  { label: 'POST', value: 'POST' },
  { label: 'PUT', value: 'PUT' },
  { label: 'PATCH', value: 'PATCH' },
  { label: 'DELETE', value: 'DELETE' },
]

const typesOptions = [
  { label: 'Notification Type', value: 'NOTIFICATION_TYPE' },
  { label: 'Advanced', value: 'ADVANCED' },
]

const tabs = [
  { key: 'params', label: 'Query Parameters' },
  { key: 'headers', label: 'Headers' },
  { key: 'body', label: 'Body' },
  { key: 'settings', label: 'Settings' },
]

type Props = {
  trigger?: NotificationTrigger;
  onSubmit?: (value: NotificationTrigger) => void;
  onCancel?: React.MouseEventHandler<HTMLButtonElement>;
}

export default function TriggerEditForm({ trigger, onSubmit, onCancel }: Props) {
  const [form] = Form.useForm();
  const [body, setBody] = useState<string>();

  useEffect(() => {
    form.setFieldsValue(trigger);
    setBody(trigger?.body);
  }, [trigger, form]);

  const handleBodyChange = useCallback((value?: string) => {
    setBody(value);
  }, []);

  const handleSubmit = (values: NotificationTrigger) => onSubmit?.({ ...values, body: JSON.parse(body || "") });

  const renderTab = useCallback((key: string) => {
    switch (key) {
      case 'params':
      default:
        return (
          <Form.List name="queryParams">
            {(fields, { add, remove }) => (
              <>
                <Row align="middle" justify="end" className="mb-2">
                  <Button type="button" variant="light" className="border" onClick={() => add()} size="sm">
                    <FontAwesomeIcon icon={solid('plus')} />
                    <span className="ms-2">Add parameter</span>
                  </Button>
                </Row>
                {fields.map((field, index) => (
                  <Form.Item key={field.key} style={{ marginBottom: '0.5rem' }}>
                    <Row align="middle">
                      <Col span={11} className="pe-2">
                        <Form.Item {...field} name={[field.name, 'key']} required noStyle>
                          <Input placeholder="Parameter key" />
                        </Form.Item>
                      </Col>
                      <Col span={11}>
                        <Form.Item {...field} name={[field.name, 'value']} required noStyle>
                          <Input placeholder="Parameter value" />
                        </Form.Item>
                      </Col>
                      <Col flex="1 0" className="text-end">
                        <IconButton type="button" onClick={() => remove(field.name)}>
                          <FontAwesomeIcon icon={solid('times')} />
                        </IconButton>
                      </Col>
                    </Row>
                  </Form.Item>
                ))}
              </>
            )}
          </Form.List>
        )

      case 'headers':
        return (
          <Form.List name="headers">
            {(fields, { add, remove }) => (
              <>
                <Row align="middle" justify="end" className="mb-2">
                  <Button type="button" variant="light" className="border" onClick={() => add()} size="sm">
                    <FontAwesomeIcon icon={solid('plus')} />
                    <span className="ms-2">Add header</span>
                  </Button>
                </Row>
                {fields.map((field, index) => (
                  <Form.Item key={field.key} style={{ marginBottom: '0.5rem' }}>
                    <Row align="middle">
                      <Col span={11} className="pe-2">
                        <Form.Item {...field} name={[field.name, 'key']} required noStyle>
                          <Input placeholder="Header key" />
                        </Form.Item>
                      </Col>
                      <Col span={11}>
                        <Form.Item {...field} name={[field.name, 'value']} required noStyle>
                          <Input placeholder="Header value" />
                        </Form.Item>
                      </Col>
                      <Col flex="1 0" className="text-end">
                        <IconButton type="button" onClick={() => remove(field.name)}>
                          <FontAwesomeIcon icon={solid('times')} />
                        </IconButton>
                      </Col>
                    </Row>
                  </Form.Item>
                ))}
              </>
            )}
          </Form.List>
        )

      case 'body':
        return <ScriptEditor value={body} onChange={handleBodyChange} />;

      case 'settings':
        return (
          <>
            <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Trigger type is required' }]}>
              <Select placeholder="Trigger type" options={typesOptions} />
            </Form.Item>

            <Form.Item noStyle dependencies={['type']}>
              {({ getFieldValue }) => (
                <>
                  <Form.Item label="Notification Type" name="notificationType">
                    <Select placeholder="Trigger Notification Type" options={notificationTypesOptions} />
                  </Form.Item>

                  {getFieldValue('type') === 'ADVANCED' && (
                    <>
                      <Form.Item name="bdaId" label="Application" rules={[{ required: true, message: 'Application is required' }]}>
                        <Input placeholder="Application (id)" />
                      </Form.Item>

                      <Form.Item name="serviceId" label="Service">
                        <Input placeholder="Service (id)" />
                      </Form.Item>

                      <Form.Item name="assetKey" label="Asset Key">
                        <Input placeholder="Asset key" />
                      </Form.Item>
                    </>
                  )}
                </>
              )}
            </Form.Item>
          </>
        )
    }
  }, [body, handleBodyChange]);

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Trigger name is required' }]}>
        <Input placeholder="Trigger name" />
      </Form.Item>

      <Form.Item noStyle>
        <Row align="middle">
          <Col span={5} className="pe-2">
            <Form.Item name="method" label="Method" rules={[{ required: true, message: 'Trigger method is required' }]}>
              <Select placeholder="Select Trigger Method" options={methodsOptions} />
            </Form.Item>
          </Col>
          <Col flex="1 0">
            <Form.Item name="url" label="URL" rules={[{ required: true, message: 'Trigger URL is required' }]}>
              <Input placeholder="Trigger URL" />
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>

      <Tabs
        defaultActiveKey="params"
        items={tabs.map(t => ({
          ...t,
          children: renderTab(t.key)
        }))}
      />

      <div className="d-flex justify-content-between mt-3">
        <Button size="sm" variant="light" className="border" onClick={onCancel}>Cancel</Button>
        <Button size="sm" variant="success" className="px-2" type="submit">
          <FontAwesomeIcon icon={solid('check')} />
          <span className="ms-2">Confirm</span>
        </Button>
      </div>
    </Form>
  )
}
